import {
  OmsExportPreset,
  OmsPresetRequest,
  OmsProduct,
  OmsReport,
} from "../../lusca-search-api/generated-src";
import { applyPreset, OmsConfiguration } from "./omsPresetEditing";
import { columnSelectionError } from "./OMSColumns";
import { filterIssue, presetNameError } from "./omsConfigurationValidation";
import { submittedCondition } from "./omsFilterEditing";

export const omsPresetCopyProducts = [
  { value: OmsProduct.Channels, label: "PV Channels", gl: 613 },
  { value: OmsProduct.Addon, label: "PV Addon", gl: 796 },
  { value: OmsProduct.Tvod, label: "PV TVOD", gl: 318 },
];

export function presetCopyNamespace(
  source: string,
  id: string,
  target: string
) {
  return `${target}:preset-copy:${source}:${id}`;
}

export function presetCopySettings(
  preset: OmsExportPreset,
  report: OmsReport
): OmsConfiguration {
  return applyPreset(
    {
      ...preset,
      filters: preset.filters?.map((filter) => ({
        ...filter,
        ...(filter.values ? { values: [...filter.values] } : {}),
      })),
    },
    {
      mode: "full",
      criteria: [],
      operation: "and",
      columns: { strategy: "all", names: [], customOrder: true },
    },
    report
  );
}

export function presetCopyIssues(
  preset: OmsExportPreset,
  name: string,
  draft: OmsConfiguration,
  report: OmsReport
): string[] {
  const issues: string[] = [];
  const nameError = presetNameError(name);
  if (nameError) issues.push(nameError);
  if (preset.scope !== "columns" && draft.mode === "filtered") {
    if (!draft.criteria.length) issues.push("Add at least one filter.");
    draft.criteria.forEach((criterion, index) => {
      const issue = filterIssue(report, criterion, index);
      if (issue) issues.push(`Filter ${index + 1}: ${issue.message}`);
    });
    if (draft.criteria.length > 20) issues.push("Use no more than 20 filters.");
  }
  if (preset.scope !== "filters") {
    const error = columnSelectionError(draft.columns, report);
    if (error) issues.push(error);
  }
  return issues;
}

export function presetCopyBody(
  preset: OmsExportPreset,
  name: string,
  draft: OmsConfiguration,
  report: OmsReport
): OmsPresetRequest {
  const issues = presetCopyIssues(preset, name, draft, report);
  if (issues.length) throw new Error(issues[0]);
  return {
    name: name.trim().normalize("NFC"),
    scope: preset.scope,
    validationSnapshotId: report.snapshotId,
    ...(preset.scope !== "columns"
      ? {
          rows: draft.mode === "full" ? "all" : "filtered",
          filters:
            draft.mode === "full" ? [] : draft.criteria.map(submittedCondition),
          operation: draft.mode === "full" ? "and" : draft.operation,
        }
      : {}),
    ...(preset.scope !== "filters"
      ? {
          columns:
            draft.columns.strategy === "all"
              ? { strategy: "all" }
              : { strategy: "selected", names: [...draft.columns.names] },
        }
      : {}),
  };
}

export function contextFiltersToReview(
  draft: OmsConfiguration,
  source: { account?: string; gl: number },
  target: { account: string; gl: number }
) {
  if (draft.mode !== "filtered") return [];
  return draft.criteria.filter(
    (filter) =>
      !["isNull", "isNotNull"].includes(filter.operator) &&
      ((filter.column === "account" && source.account !== target.account) ||
        (filter.column === "gl_product_line" && source.gl !== target.gl))
  );
}
